import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Getting started with the Monofor REST API',
    metaDescription:
      'Provision a client, request a token, make your first call, and wire identity into your platform. Four-step walkthrough with deep-link to the API reference.',
    heroEyebrow: 'How-to · REST API',
    heroTitle: ['The Monofor REST API', 'in four moves.'],
    heroBody:
      'Provision a client, fetch a token, call /users, then wire identity into your platform. Standard REST conventions throughout: no SDK gymnastics.',
    badges: ['OAuth client-credentials', 'Cursor pagination', '~8 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the API reference',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'Auth, list, mutate, automate.',
    stepsBody:
      'The mental model. The endpoint reference and SDK examples live in the help center.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Provision an API client',
        body: 'In Monosign, create a service-account credential or OAuth client with the scopes your integration needs. Stash the client ID and secret in your secrets manager, never in the repo.',
        hint: 'For long-running automation, prefer OAuth client-credentials with short TTLs over static API keys.',
      },
      {
        title: 'Request an access token',
        body: 'POST to the token endpoint with the client credentials. You get back a short-lived bearer token. Cache it for the TTL minus a small buffer; that single round-trip saves you a request per call.',
      },
      {
        title: 'Make your first call',
        body: 'GET /users to confirm the token works. The response is cursor-paginated; follow next_cursor when you need more than one page.',
        hint: 'Pass an idempotency-key header on POST and PATCH calls so retries are safe.',
      },
      {
        title: 'Wire it into your platform',
        body: 'Most teams start by scripting joiner / mover / leaver events into the API, then layer in webhook subscriptions so identity changes drive downstream automation in real time.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Monosync API', description: 'Identity as a queryable resource.', href: '/sync/api' },
      { label: 'For Developers', description: 'OAuth 2, OIDC, SAML, JWT, SDKs.', href: '/for/developers' },
      { label: 'Single Sign-On', description: 'The SSO surface the API sits next to.', href: '/sign/sso' },
    ],
  },
  tr: {
    metaTitle: "Monofor REST API'ye başlangıç",
    metaDescription:
      'Bir istemci oluşturun, token alın, ilk çağrınızı yapın ve kimliği platformunuza bağlayın. Dört adımlık rehber ve API referansına derin bağlantı.',
    heroEyebrow: 'Rehber · REST API',
    heroTitle: ['Dört hamlede', 'Monofor REST API.'],
    heroBody:
      'Bir istemci oluşturun, token alın, /users çağrısı yapın, sonra kimliği platformunuza bağlayın. Baştan sona standart REST kuralları: SDK cambazlığı yok.',
    badges: ['OAuth client-credentials', 'Cursor tabanlı sayfalama', '~8 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'API referansını açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'Doğrula, listele, değiştir, otomatikleştir.',
    stepsBody:
      "Zihinsel model bu. Endpoint referansı ve SDK örnekleri yardım merkezinde.",
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: 'Bir API istemcisi oluşturun',
        body: "Monosign'da, entegrasyonunuzun ihtiyaç duyduğu scope'larla bir servis hesabı kimlik bilgisi veya OAuth istemcisi oluşturun. Client ID ve secret'ı secrets manager'ınızda saklayın, asla repoda değil.",
        hint: "Uzun süreli otomasyonlar için statik API anahtarları yerine kısa TTL'li OAuth client-credentials tercih edin.",
      },
      {
        title: "Erişim token'ı alın",
        body: "Token endpoint'ine client credentials ile POST isteği gönderin. Karşılığında kısa ömürlü bir bearer token alırsınız. TTL'den küçük bir pay düşerek önbelleğe alın; bu tek gidiş-dönüş, her çağrıda size bir istek kazandırır.",
      },
      {
        title: 'İlk çağrınızı yapın',
        body: "Token'ın çalıştığını doğrulamak için GET /users çağrısı yapın. Yanıt cursor ile sayfalanır; birden fazla sayfa gerektiğinde next_cursor'ı izleyin.",
        hint: 'POST ve PATCH çağrılarında idempotency-key başlığı geçirin; böylece yeniden denemeler güvenli olur.',
      },
      {
        title: 'Platformunuza bağlayın',
        body: "Çoğu ekip işe giriş / geçiş / ayrılış olaylarını API'ye script ile bağlayarak başlar, ardından kimlik değişikliklerinin aşağı akış otomasyonunu gerçek zamanlı tetiklemesi için webhook abonelikleri ekler.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Monosync API', description: 'Sorgulanabilir bir kaynak olarak kimlik.', href: '/sync/api' },
      { label: 'Geliştiriciler için', description: "OAuth 2, OIDC, SAML, JWT, SDK'lar.", href: '/for/developers' },
      { label: 'Tek Oturum Açma (SSO)', description: "API'nin yanı başındaki SSO yüzeyi.", href: '/sign/sso' },
    ],
  },
}

type Props = { locale: string }

export default function RestApiGettingStartedPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/rest-api-getting-started"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/rest-api' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/rest-api"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
